import {FC, useMemo, useState} from 'react';
import {Button, DialogActions, DialogContent, TextField} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  ChatMessage,
  useCreateChatAnnouncement,
  useCreateChatBan,
  useDeleteChatMessage,
  useGetTopicChatChannel,
  useGetTopicChatMessages,
  useTopicChatUpdates,
} from '@entities/chat';
import {
  Dialog,
  DialogTitle,
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {PaginationState} from '@shared/types';
import {formatInTz} from '@shared/utils';

interface Props {
  topicSlug: string;
}

export const ChatTab: FC<Props> = ({topicSlug}) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isAnnouncementDialogOpen, setIsAnnouncementDialogOpen] =
    useState(false);
  const [announcementText, setAnnouncementText] = useState('');
  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {data, isPending, error} = useGetTopicChatMessages({
    topicSlug,
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });
  const {data: channel} = useGetTopicChatChannel(topicSlug);
  useTopicChatUpdates(topicSlug, channel?.id);
  const {mutateAsync: deleteMessage, isPending: isDeleting} =
    useDeleteChatMessage(topicSlug);
  const {mutateAsync: createBan, isPending: isBanning} = useCreateChatBan();
  const {mutateAsync: createAnnouncement, isPending: isAnnouncing} =
    useCreateChatAnnouncement();

  const handleDelete = async (message: ChatMessage) => {
    if (!channel || message.deleted) return;

    try {
      await openConfirmation({
        title: 'Delete message',
        text: 'Are you sure you want to delete this message?',
        okButtonText: 'Delete',
      });
    } catch {
      return;
    }

    try {
      await deleteMessage({channelId: channel.id, messageId: message.id});
      enqueueSnackbar('Message deleted successfully', {variant: 'success'});
    } catch {
      enqueueSnackbar('Failed to delete message', {variant: 'error'});
    }
  };

  const handleBan = async (message: ChatMessage) => {
    if (!channel || !message.author) return;

    try {
      await openConfirmation({
        title: 'Ban author',
        text: `Ban ${message.author.username} from this topic chat?`,
        okButtonText: 'Ban',
      });
    } catch {
      return;
    }

    try {
      await createBan({userId: message.author.id, channelId: channel.id});
      enqueueSnackbar('Author banned successfully', {variant: 'success'});
    } catch {
      enqueueSnackbar('Failed to ban author', {variant: 'error'});
    }
  };

  const closeAnnouncementDialog = () => {
    if (isAnnouncing) return;
    setAnnouncementText('');
    setIsAnnouncementDialogOpen(false);
  };

  const handleAnnouncement = async () => {
    if (!channel || !announcementText.trim()) return;

    try {
      await createAnnouncement({
        channelId: channel.id,
        text: announcementText.trim(),
      });
      enqueueSnackbar('Announcement sent successfully', {variant: 'success'});
      closeAnnouncementDialog();
    } catch {
      enqueueSnackbar('Failed to send announcement', {variant: 'error'});
    }
  };

  const columns = useMemo<MRT_ColumnDef<ChatMessage>[]>(
    () => [
      {
        accessorKey: 'author.username',
        header: 'Author',
        size: 180,
        Cell: ({row}) => row.original.author?.username ?? 'System',
      },
      {
        accessorKey: 'text',
        header: 'Message',
        Cell: ({row}) => row.original.text ?? row.original.systemKind ?? '-',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        size: 180,
        Cell: ({row}) => formatInTz(row.original.createdAt, DATETIME_FORMAT),
      },
    ],
    [],
  );

  return (
    <>
      <MrTable
        columns={columns}
        data={data?.items ?? []}
        error={error}
        renderTopToolbarCustomActions={() => (
          <Button
            onClick={() => setIsAnnouncementDialogOpen(true)}
            disabled={!channel}
          >
            Announcement
          </Button>
        )}
        renderRowActions={({row}) => (
          <TableRowActionsContainer>
            <Button
              variant="text"
              color="secondary"
              disabled={!channel || row.original.deleted || isDeleting}
              onClick={() => void handleDelete(row.original)}
            >
              Delete
            </Button>
            <Button
              variant="text"
              color="secondary"
              disabled={!channel || !row.original.author || isBanning}
              onClick={() => void handleBan(row.original)}
            >
              Ban author
            </Button>
          </TableRowActionsContainer>
        )}
        enablePagination
        manualPagination
        onPaginationChange={setUrl}
        rowCount={data?.total ?? 0}
        state={{isLoading: isPending, pagination: urlState}}
        {...entityTableCommonProps}
      />

      <Dialog open={isAnnouncementDialogOpen} onClose={closeAnnouncementDialog}>
        <DialogTitle title="Announcement" sx={{px: 3, pt: 3}} />
        <DialogContent
          sx={{
            display: 'flex',
            width: 700,
            maxWidth: '80vw',
            height: 280,
            boxSizing: 'border-box',
            px: 3,
            pt: '20px !important',
            pb: 0,
          }}
        >
          <TextField
            autoFocus
            multiline
            fullWidth
            value={announcementText}
            onChange={event => setAnnouncementText(event.target.value)}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
              '& textarea': {
                height: '100% !important',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{justifyContent: 'flex-end', px: 3, pb: 3}}>
          <Button
            variant="outlined"
            onClick={closeAnnouncementDialog}
            disabled={isAnnouncing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleAnnouncement()}
            disabled={isAnnouncing || !announcementText.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
